import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import {
  Heart,
  Globe,
  MapPin,
  Microphone,
  Translate,
  Link as LinkIcon,
  Play,
  Pause,
  ChatCircle,
  ThumbsUp,
  Trash,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { motion } from 'framer-motion';

const stationDetailTexts = {
  de: {
    stationNotFound: 'Sender nicht gefunden',
    visitWebsite: 'Website besuchen',
    playStation: 'Sender abspielen',
    pauseStation: 'Sender pausieren',
    comments: 'Kommentare',
    shareThoughts: 'Teile deine Gedanken...',
    postComment: 'Kommentar posten',
    loginToComment: 'Melde dich an, um einen Kommentar zu schreiben',
    login: 'Einloggen',
    noComments:
      'Noch keine Kommentare. Sei der Erste, der seine Gedanken teilt!',
  },

  en: {
    stationNotFound: 'Station not found',
    visitWebsite: 'Visit Website',
    playStation: 'Play Station',
    pauseStation: 'Pause Station',
    comments: 'Comments',
    shareThoughts: 'Share your thoughts...',
    postComment: 'Post Comment',
    loginToComment: 'Log in to leave a comment',
    login: 'Log in',
    noComments: 'No comments yet. Be the first to share your thoughts!',
  },

  no: {
    stationNotFound: 'Stasjonen ble ikke funnet',
    visitWebsite: 'Besøk nettside',
    playStation: 'Spill stasjon',
    pauseStation: 'Pause stasjon',
    comments: 'Kommentarer',
    shareThoughts: 'Del tankene dine...',
    postComment: 'Legg ut kommentar',
    loginToComment: 'Logg inn for å legge igjen en kommentar',
    login: 'Logg inn',
    noComments:
      'Ingen kommentarer ennå. Vær den første som deler tankene dine!',
  },

  ko: {
    stationNotFound: '방송국을 찾을 수 없습니다',
    visitWebsite: '웹사이트 방문',
    playStation: '방송국 재생',
    pauseStation: '방송국 일시정지',
    comments: '댓글',
    shareThoughts: '생각을 공유하세요...',
    postComment: '댓글 게시',
    loginToComment: '댓글을 남기려면 로그인하세요',
    login: '로그인',
    noComments: '아직 댓글이 없습니다. 첫 번째로 의견을 공유해 보세요!',
  },

  fi: {
    stationNotFound: 'Asemaa ei löytynyt',
    visitWebsite: 'Vieraile sivustolla',
    playStation: 'Toista asema',
    pauseStation: 'Keskeytä asema',
    comments: 'Kommentit',
    shareThoughts: 'Jaa ajatuksesi...',
    postComment: 'Lähetä kommentti',
    loginToComment: 'Kirjaudu sisään kommentoidaksesi',
    login: 'Kirjaudu sisään',
    noComments:
      'Ei kommentteja vielä. Ole ensimmäinen, joka jakaa ajatuksensa!',
  },
};

const StationDetail = () => {
  const { id } = useParams();
  const [station, setStation] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { playStation, currentStation, isPlaying } = useAudioPlayer();
  const { language } = useLanguage();

  const text = stationDetailTexts[language] || stationDetailTexts.en;
  const isCurrentlyPlaying = currentStation?.station_id === id && isPlaying;

  useEffect(() => {
    loadStation();
    loadComments();
    checkFavorite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadStation = async () => {
    try {
      const res = await api.getStation(id);
      setStation(res.data);
    } catch (error) {
      console.error('Failed to load station:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const res = await api.getComments(id);
      setComments(res.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const checkFavorite = async () => {
    if (!user) return;

    try {
      const res = await api.getFavorites();
      setIsFavorite(res.data.some((s) => s.station_id === id));
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await api.removeFavorite(id);
        setIsFavorite(false);
      } else {
        await api.addFavorite(id);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!newComment.trim()) return;

    try {
      await api.createComment(id, newComment);
      setNewComment('');
      loadComments();
    } catch (error) {
      console.error('Failed to post comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.deleteComment(commentId);
      loadComments();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleLikeComment = async (commentId, isLiked) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isLiked) {
        await api.unlikeComment(commentId);
      } else {
        await api.likeComment(commentId);
      }

      loadComments();
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />

        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <p className="text-gray-400">{text.stationNotFound}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-40 md:pb-28">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        {/* Station Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-64 h-64 rounded-2xl overflow-hidden flex-shrink-0">
              <img
                src={
                  station.logo_url ||
                  'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=600'
                }
                alt={station.name}
                onError={(e) => {
                  e.target.src =
                    'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=600';
                }}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1
                    className="text-3xl sm:text-4xl tracking-tighter font-black text-white mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {station.name}
                  </h1>

                  <p className="text-base text-gray-300">
                    {station.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Globe size={18} className="text-cyan-400" />
                  {station.country}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <MapPin size={18} className="text-cyan-400" />
                  {station.city}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Microphone size={18} className="text-cyan-400" />
                  {station.genre}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Translate size={18} className="text-cyan-400" />
                  {station.language}
                </div>
              </div>

              {station.website && (
                <a
                  href={station.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 mb-6"
                >
                  <LinkIcon size={16} />
                  {text.visitWebsite}
                </a>
              )}

              <div className="flex gap-3">
                <Button
                  data-testid="play-station-btn"
                  onClick={() => playStation(station)}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center gap-2 hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all"
                >
                  {isCurrentlyPlaying ? (
                    <>
                      <Pause size={20} weight="fill" />
                      {text.pauseStation}
                    </>
                  ) : (
                    <>
                      <Play size={20} weight="fill" />
                      {text.playStation}
                    </>
                  )}
                </Button>

                <Button
                  data-testid="favorite-station-btn"
                  onClick={toggleFavorite}
                  variant="outline"
                  className={`rounded-full border-white/10 ${isFavorite
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                >
                  <Heart
                    size={20}
                    weight={isFavorite ? 'fill' : 'regular'}
                    className={isFavorite ? 'text-cyan-400' : ''}
                  />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Comments Section */}
        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <ChatCircle size={28} weight="duotone" className="text-cyan-400" />

            <h2
              className="text-2xl tracking-tight font-bold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {text.comments} ({comments.length})
            </h2>
          </div>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleCommentSubmit} className="mb-8">
              <Textarea
                data-testid="comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={text.shareThoughts}
                className="bg-[#0A0E17] border-white/10 text-white min-h-24 rounded-xl resize-none focus:border-cyan-500 focus:ring-cyan-500/20 mb-3"
              />

              <Button
                data-testid="comment-submit-btn"
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full"
              >
                {text.postComment}
              </Button>
            </form>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center mb-8">
              <p className="text-gray-300 mb-4">{text.loginToComment}</p>

              <Button
                onClick={() => navigate('/login')}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full"
              >
                {text.login}
              </Button>
            </div>
          )}

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-center text-gray-400 py-8">
                {text.noComments}
              </p>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.comment_id}
                  data-testid={`comment-${comment.comment_id}`}
                  className="bg-[#0A0E17] border border-white/5 rounded-xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold flex-shrink-0">
                      {comment.user?.name?.charAt(0).toUpperCase()}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-semibold text-white">
                            {comment.user?.name}
                          </span>
                          <span className="text-sm text-gray-500 ml-2">
                            @{comment.user?.username}
                          </span>
                        </div>

                        {user &&
                          (user.user_id === comment.user_id ||
                            user.role === 'admin') && (
                            <button
                              data-testid={`delete-comment-${comment.comment_id}`}
                              onClick={() =>
                                handleDeleteComment(comment.comment_id)
                              }
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash size={18} />
                            </button>
                          )}
                      </div>

                      <p className="text-gray-300 mb-3">{comment.content}</p>

                      <div className="flex items-center gap-4">
                        <button
                          data-testid={`like-comment-${comment.comment_id}`}
                          onClick={() =>
                            handleLikeComment(comment.comment_id, false)
                          }
                          className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                        >
                          <ThumbsUp size={16} />
                          <span>{comment.likes_count || 0}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StationDetail;